import React, { useEffect, useRef, useState } from 'react';
import '../styles/contact-form.css';

// Submissions go to our forms-service (github.com/mesmalif/forms-service), which saves each one
// and emails info@moneliautomation.com with the website, page and form it came from.
export const FORMS_URL =
  process.env.REACT_APP_FORMS_URL ||
  'https://forms-api.thankfulcoast-3792dede.eastus.azurecontainerapps.io/v1/f/moneli-contact';

const TOPICS = [
  'Phone answering',
  'Visitor / staff check-in',
  'AI team assistant',
  'Contacts & CRM',
  'Appointments / reminders',
  'Integrations',
  'MoneliLLM (private AI)',
  'Something else',
];

const EMPTY = { name: '', email: '', organization: '', phone: '', topic: '', message: '' };

const ERRORS = {
  required: 'Please fill this in.',
  invalid_email: 'Please enter a valid email address.',
};

function ContactForm({ idPrefix = 'cf', autoFocus = false, onSent }) {
  const [values, setValues] = useState(EMPTY);
  const [fieldErrors, setFieldErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [errorText, setErrorText] = useState('');
  const openedAt = useRef(Date.now());
  const trapRef = useRef(null);
  const firstRef = useRef(null);
  const successRef = useRef(null);

  useEffect(() => {
    if (autoFocus && firstRef.current) firstRef.current.focus({ preventScroll: true });
  }, [autoFocus]);

  useEffect(() => {
    if (status === 'sent' && successRef.current) successRef.current.focus({ preventScroll: true });
  }, [status]);

  const set = (e) => {
    const { name, value } = e.target;
    setValues((v) => ({ ...v, [name]: value }));
    if (fieldErrors[name]) setFieldErrors((f) => ({ ...f, [name]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!values.name.trim()) errs.name = 'required';
    if (!values.email.trim()) errs.email = 'required';
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(values.email.trim())) errs.email = 'invalid_email';
    if (!values.message.trim()) errs.message = 'required';
    return errs;
  };

  const focusField = (name) => {
    const el = document.getElementById(`${idPrefix}-${name}`);
    if (el) el.focus();
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length) { focusField(Object.keys(errs)[0]); return; }

    setStatus('sending');
    setErrorText('');
    try {
      const res = await fetch(FORMS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          ...values,
          _gotcha: trapRef.current ? trapRef.current.value : '',
          _t: String(Date.now() - openedAt.current),
          _page: window.location.href,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        setStatus('sent');
        setValues(EMPTY);
        if (typeof window.gtag === 'function') window.gtag('event', 'form_submit', { form_type: 'contact', topic: values.topic || 'none' });
        if (onSent) onSent();
        return;
      }
      if (data.fields) {
        setFieldErrors(data.fields);
        focusField(Object.keys(data.fields)[0]);
        setStatus('idle');
        return;
      }
      throw new Error(data.error === 'rate_limited'
        ? 'Too many messages in a short time. Please try again in a minute.'
        : 'We could not send your message.');
    } catch (err) {
      setStatus('error');
      setErrorText(err && err.message && !/fetch|network/i.test(err.message) ? err.message : 'We could not send your message.');
    }
  };

  if (status === 'sent') {
    return (
      <div className="cform-success" ref={successRef} tabIndex={-1} role="status">
        <span className="cform-success__icon" aria-hidden="true"><i className="fa-solid fa-check" /></span>
        <h3 className="cform-success__title">Message sent</h3>
        <p className="cform-success__text">Thanks for reaching out. We usually reply within 2 hours on business days.</p>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => { openedAt.current = Date.now(); setStatus('idle'); }}>
          Send another message
        </button>
      </div>
    );
  }

  const err = (name) => fieldErrors[name] && (
    <p className="cform__error" id={`${idPrefix}-${name}-err`}>{ERRORS[fieldErrors[name]] || 'Please check this field.'}</p>
  );
  const aria = (name) => (fieldErrors[name]
    ? { 'aria-invalid': 'true', 'aria-describedby': `${idPrefix}-${name}-err` }
    : {});
  const sending = status === 'sending';

  return (
    <form className="cform" onSubmit={submit} noValidate aria-busy={sending}>
      <div className="cform__grid">
        <div className="cform__field">
          <label className="form-label" htmlFor={`${idPrefix}-name`}>Name <span className="cform__req" aria-hidden="true">*</span></label>
          <input ref={firstRef} className="form-control" id={`${idPrefix}-name`} name="name" autoComplete="name"
            required maxLength={120} value={values.name} onChange={set} {...aria('name')} />
          {err('name')}
        </div>
        <div className="cform__field">
          <label className="form-label" htmlFor={`${idPrefix}-email`}>Email <span className="cform__req" aria-hidden="true">*</span></label>
          <input className="form-control" id={`${idPrefix}-email`} name="email" type="email" autoComplete="email"
            required maxLength={200} value={values.email} onChange={set} {...aria('email')} />
          {err('email')}
        </div>
        <div className="cform__field">
          <label className="form-label" htmlFor={`${idPrefix}-organization`}>Organization</label>
          <input className="form-control" id={`${idPrefix}-organization`} name="organization" autoComplete="organization"
            maxLength={200} value={values.organization} onChange={set} />
        </div>
        <div className="cform__field">
          <label className="form-label" htmlFor={`${idPrefix}-phone`}>Phone</label>
          <input className="form-control" id={`${idPrefix}-phone`} name="phone" type="tel" autoComplete="tel"
            maxLength={40} value={values.phone} onChange={set} />
        </div>
        <div className="cform__field cform__field--full">
          <label className="form-label" htmlFor={`${idPrefix}-topic`}>What are you interested in?</label>
          <select className="form-select" id={`${idPrefix}-topic`} name="topic" value={values.topic} onChange={set}>
            <option value="">Choose one (optional)</option>
            {TOPICS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="cform__field cform__field--full">
          <label className="form-label" htmlFor={`${idPrefix}-message`}>How can we help? <span className="cform__req" aria-hidden="true">*</span></label>
          <textarea className="form-control" id={`${idPrefix}-message`} name="message" rows={4} required maxLength={5000}
            value={values.message} onChange={set} {...aria('message')} />
          {err('message')}
        </div>
      </div>

      {/* Honeypot: people never see or fill this; bots do. */}
      <input ref={trapRef} type="text" name="_gotcha" tabIndex={-1} autoComplete="off" aria-hidden="true" className="cform__trap" defaultValue="" />

      {status === 'error' && (
        <p className="cform__alert" role="alert">
          {errorText} Please try again, or email{' '}
          <a href="mailto:info@moneliautomation.com">info@moneliautomation.com</a>.
        </p>
      )}

      <div className="cform__foot">
        <button type="submit" className="btn btn-primary btn-lg" disabled={sending} data-magnetic>
          {sending ? <><span className="cform__spinner" aria-hidden="true" /> Sending…</> : <>Send message <i className="fa-solid fa-paper-plane" aria-hidden="true" /></>}
        </button>
        <p className="cform__note">We reply within 2 hours on business days. Your details are only used to answer you.</p>
      </div>
    </form>
  );
}

export default ContactForm;
